/**
 * Centralized API Client
 *
 * All API requests should go through this client to ensure:
 * - Auth tokens are automatically included
 * - Consistent error handling
 * - Single source of truth for API configuration
 */

import { getAPIHeadersWithAuth } from './apiHeaders'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export interface ApiClientOptions extends Omit<RequestInit, 'body'> {
  body?: any
  // If true, returns raw Response instead of parsed JSON
  rawResponse?: boolean
  // If true, expects text response instead of JSON
  textResponse?: boolean
}

export interface ApiError extends Error {
  status?: number
  statusText?: string
  code?: string
  data?: any
}

/**
 * Create an API error with additional context
 */
function createApiError(message: string, status?: number, statusText?: string, data?: any): ApiError {
  const error = new Error(message) as ApiError
  error.status = status
  error.statusText = statusText
  error.data = data
  if (data?.code) {
    error.code = data.code
  }
  return error
}

/**
 * Core fetch function with auth headers
 */
async function fetchWithAuth(endpoint: string, options: ApiClientOptions = {}): Promise<Response> {
  const { body, rawResponse, textResponse, ...fetchOptions } = options

  const headers = await getAPIHeadersWithAuth()

  const config: RequestInit = {
    ...fetchOptions,
    headers: {
      ...headers,
      ...fetchOptions.headers
    }
  }

  // Handle body - stringify if object
  if (body !== undefined) {
    config.body = typeof body === 'string' ? body : JSON.stringify(body)
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`
  return fetch(url, config)
}

/**
 * Main API client object with HTTP method helpers
 */
export const apiClient = {
  /**
   * GET request
   */
  async get<T = any>(endpoint: string, options: ApiClientOptions = {}): Promise<T> {
    const response = await fetchWithAuth(endpoint, { ...options, method: 'GET' })

    if (options.rawResponse) {
      return response as unknown as T
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }))
      throw createApiError(
        errorData.error || errorData.message || `HTTP ${response.status}`,
        response.status,
        response.statusText,
        errorData
      )
    }

    if (options.textResponse) {
      return await response.text() as unknown as T
    }

    return response.json()
  },

  /**
   * POST request
   */
  async post<T = any>(endpoint: string, body?: any, options: ApiClientOptions = {}): Promise<T> {
    const response = await fetchWithAuth(endpoint, { ...options, method: 'POST', body })

    if (options.rawResponse) {
      return response as unknown as T
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }))
      throw createApiError(
        errorData.error || errorData.message || `HTTP ${response.status}`,
        response.status,
        response.statusText,
        errorData
      )
    }

    if (options.textResponse) {
      return await response.text() as unknown as T
    }

    return response.json()
  },

  /**
   * PUT request
   */
  async put<T = any>(endpoint: string, body?: any, options: ApiClientOptions = {}): Promise<T> {
    const response = await fetchWithAuth(endpoint, { ...options, method: 'PUT', body })

    if (options.rawResponse) {
      return response as unknown as T
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }))
      throw createApiError(
        errorData.error || errorData.message || `HTTP ${response.status}`,
        response.status,
        response.statusText,
        errorData
      )
    }

    if (options.textResponse) {
      return await response.text() as unknown as T
    }

    return response.json()
  },

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string, options: ApiClientOptions = {}): Promise<T> {
    const response = await fetchWithAuth(endpoint, { ...options, method: 'DELETE' })

    if (options.rawResponse) {
      return response as unknown as T
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }))
      throw createApiError(
        errorData.error || errorData.message || `HTTP ${response.status}`,
        response.status,
        response.statusText,
        errorData
      )
    }

    if (options.textResponse) {
      return await response.text() as unknown as T
    }

    return response.json()
  },

  /**
   * Raw fetch with auth - for cases needing full response control
   * (streaming, custom response handling, etc.)
   */
  async fetch(endpoint: string, options: ApiClientOptions = {}): Promise<Response> {
    return fetchWithAuth(endpoint, options)
  },

  /**
   * Get the base URL for constructing endpoints
   */
  getBaseUrl(): string {
    return API_BASE_URL
  }
}

export default apiClient
